import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  MoneyInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@mlm/ui';
import { Decimal, normalizeSku, skuFromName } from '@mlm/shared';
import {
  apiErrorMessage,
  apiErrorPayload,
  createProduct,
  getTenantFeatures,
  listProducts,
  updateProduct,
  type Product,
  type SkuUsage,
} from '../lib/api';
import { Field, ProductSuppliersSection, SkuRenameConfirmDialog } from './products';

/** Same handling as the product editor: the confirm dialog IS this error's UI. */
const SKU_RENAME_SILENT = ['sku_in_use'];

/** What the material form submits. Everything sale-side is fixed by the server. */
type MaterialDetails = {
  sku: string;
  name: string;
  description?: string;
  unitLabel: string | null;
  packSize: number | null;
  cost: string | null;
  /** Only sent while stock lots are on (the field is hidden otherwise). */
  shelfLifeDays?: number | null;
  confirmSkuRename?: boolean;
};

/**
 * Materials: supplies that are stocked and costed but never sold (cups, lids,
 * coffee beans). A product's Recipe tab names the materials one sold unit uses
 * up, and selling the product takes them off the branch's shelf.
 *
 * A material is a row of the same `products` table (`is_material`), so
 * receiving, write-offs, purchase orders, suppliers and the stock log all work
 * on it unchanged. This page is a third partition of the catalog, next to
 * Products and Virtual Products, with a form trimmed to what a material has:
 * no price, no PV, no dimensions, no kit or variation settings.
 */
export function MaterialsPage() {
  // The sidebar entry is already gated, but the URL is still typeable and
  // bookmarkable. Say so plainly instead of rendering an empty catalog.
  const featuresQuery = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  if (featuresQuery.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (featuresQuery.data?.materials_enabled !== true) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Materials are turned off for this tenant. Turn on{' '}
          <strong>Enable materials and recipes</strong> in Settings → System to track supplies and
          build product recipes here.
        </CardContent>
      </Card>
    );
  }
  return <MaterialsCatalog />;
}

function MaterialsCatalog() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'active' | 'archived' | 'all'>('active');
  const [creating, setCreating] = useState(false);
  // Hold the row itself, not an id, so a refetch or a search change can never
  // flip the open dialog into create mode (the duplicate-SKU bug the product
  // editor documents).
  const [editing, setEditing] = useState<Product | null>(null);

  const materialsQuery = useQuery({
    // Under the `admin-products` prefix on purpose: a product save invalidates
    // that prefix, and this list is the same table.
    queryKey: ['admin-products', 'materials', search],
    queryFn: () => listProducts({ pageSize: 100, search: search || undefined, material: 'only' }),
  });

  const all = materialsQuery.data ?? [];
  const visible = all.filter((m) =>
    status === 'all' ? true : status === 'active' ? m.active : !m.active,
  );
  const archivedCount = all.filter((m) => !m.active).length;

  const toggleActive = useMutation({
    mutationFn: (m: Product) => updateProduct(m.id, { active: !m.active }),
    meta: { successMessage: 'Material updated.' },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-products'] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Materials</h1>
          <p className="text-sm text-muted-foreground">
            Supplies that are stocked but never sold: cups, lids, ingredients. Add a material here,
            receive its stock on a branch like any product, then name it in a product’s Recipe tab.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {status === 'active' && archivedCount > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setStatus('archived')}>
              View {archivedCount} archived
            </Button>
          )}
          <Button onClick={() => setCreating(true)}>+ New material</Button>
        </div>
      </div>

      <Card>
        <CardContent className="grid grid-cols-1 gap-3 pt-6 md:grid-cols-4">
          <div className="space-y-1 md:col-span-3">
            <label className="text-xs text-muted-foreground" htmlFor="material-search">
              Search
            </label>
            <Input
              id="material-search"
              placeholder="Name or SKU"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Status</label>
            <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {creating && <MaterialDialog mode="create" onClose={() => setCreating(false)} />}
      {editing && (
        <MaterialDialog
          mode="edit"
          key={editing.id}
          material={all.find((m) => m.id === editing.id) ?? editing}
          onClose={() => setEditing(null)}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Catalog</CardTitle>
        </CardHeader>
        <CardContent>
          {materialsQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : visible.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {all.length === 0 && !search
                ? 'No materials yet. Add the first one with “New material”.'
                : 'No materials match.'}
            </p>
          ) : (
            <ul className="divide-y">
              {visible.map((m) => (
                <li key={m.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium">{m.name}</span>
                      {!m.active && (
                        <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                          Archived
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">{materialSummary(m)}</div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setEditing(m)}>
                    Edit
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => toggleActive.mutate(m)}>
                    {m.active ? 'Archive' : 'Restore'}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/** "CUP-12OZ · counted in pcs · pack of 50 · cost ₱0.50 each". */
function materialSummary(m: Product): string {
  const unit = m.unitLabel ?? 'pcs';
  const parts = [m.sku, `counted in ${unit}`];
  if (m.packSize) parts.push(`pack of ${m.packSize.toLocaleString()}`);
  // "unknown" rather than ₱0.00: a blank cost is not a free material.
  parts.push(
    m.cost != null ? `cost ₱${new Decimal(m.cost).toString()} per ${unit}` : 'no cost set',
  );
  return parts.join(' · ');
}

function MaterialDialog({
  mode,
  material,
  onClose,
}: {
  mode: 'create' | 'edit';
  material?: Product;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  // In create mode the row is persisted on the first save and held here so the
  // Suppliers tab (which keys off its id) can open.
  const [created, setCreated] = useState<Product | null>(null);
  const current = material ?? created;
  const [tab, setTab] = useState('info');

  const create = useMutation({
    mutationFn: (d: MaterialDetails) =>
      createProduct({
        sku: d.sku,
        name: d.name,
        // The route's `description` is a plain optional string, not nullable.
        ...(d.description ? { description: d.description } : {}),
        // A material is never sold. The server forces every sale-side field off
        // whatever is sent; a zero price and PV are simply the honest values.
        price: '0',
        pv: '0',
        isMaterial: true,
        unitLabel: d.unitLabel,
        packSize: d.packSize,
        cost: d.cost,
        ...(d.shelfLifeDays !== undefined ? { shelfLifeDays: d.shelfLifeDays } : {}),
      }),
    meta: { successMessage: 'Material created.' },
    onSuccess: (p) => {
      queryClient.invalidateQueries({ queryKey: ['admin-products'] });
      setCreated(p);
    },
  });

  // A SKU rename on a material that already has stock history comes back as
  // 409 `sku_in_use`. Park the payload and re-send it with `confirmSkuRename`
  // once the admin agrees, exactly like the product editor.
  const [skuRenameConfirm, setSkuRenameConfirm] = useState<{
    details: MaterialDetails;
    usage?: SkuUsage;
  } | null>(null);
  const update = useMutation({
    mutationFn: (d: MaterialDetails) =>
      updateProduct(current!.id, {
        sku: d.sku,
        name: d.name,
        // An empty string clears it; the route does not accept null here.
        description: d.description ?? '',
        unitLabel: d.unitLabel,
        packSize: d.packSize,
        cost: d.cost,
        ...(d.shelfLifeDays !== undefined ? { shelfLifeDays: d.shelfLifeDays } : {}),
        ...(d.confirmSkuRename ? { confirmSkuRename: true } : {}),
      }),
    meta: { successMessage: 'Material saved.', silentErrorCodes: SKU_RENAME_SILENT },
    onSuccess: (p) => {
      queryClient.invalidateQueries({ queryKey: ['admin-products'] });
      if (created) setCreated(p);
      setSkuRenameConfirm(null);
    },
    onError: (err, details) => {
      const payload = apiErrorPayload(err);
      if (payload?.error === 'sku_in_use') {
        setSkuRenameConfirm({ details, usage: payload.usage as SkuUsage | undefined });
      }
    },
  });
  const skuRenameHandled = SKU_RENAME_SILENT.includes(apiErrorPayload(update.error)?.error ?? '');
  const submitting = create.isPending || update.isPending;

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'New material' : `Edit ${material?.name ?? 'material'}`}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create' && !current
              ? 'Save the material first, then link its suppliers.'
              : 'Each tab saves independently.'}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-1">
          <TabsList>
            <TabsTrigger value="info">Material</TabsTrigger>
            <TabsTrigger value="suppliers" disabled={!current}>
              Suppliers
            </TabsTrigger>
          </TabsList>

          <TabsContent value="info">
            <MaterialForm
              material={current ?? undefined}
              submitting={submitting}
              submitLabel={submitting ? 'Saving…' : current ? 'Save' : 'Save material'}
              saved={(create.isSuccess || update.isSuccess) && !submitting}
              error={create.error ?? (skuRenameHandled ? undefined : update.error)}
              // Edit mode ALWAYS updates; create mode creates first, then updates
              // the row it just made. Never key this on truthiness alone.
              onSubmit={(d) => (mode === 'edit' || current ? update.mutate(d) : create.mutate(d))}
              onCancel={onClose}
            />
          </TabsContent>

          {current && (
            <TabsContent value="suppliers">
              <ProductSuppliersSection productId={current.id} />
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>

      {skuRenameConfirm && (
        <SkuRenameConfirmDialog
          usage={skuRenameConfirm.usage}
          oldSku={current?.sku ?? ''}
          newSku={skuRenameConfirm.details.sku}
          submitting={update.isPending}
          onCancel={() => {
            update.reset();
            setSkuRenameConfirm(null);
          }}
          onConfirm={() => update.mutate({ ...skuRenameConfirm.details, confirmSkuRename: true })}
        />
      )}
    </Dialog>
  );
}

function MaterialForm({
  material,
  submitting,
  submitLabel,
  saved,
  error,
  onSubmit,
  onCancel,
}: {
  material?: Product;
  submitting: boolean;
  submitLabel: string;
  saved?: boolean;
  error: unknown;
  onSubmit: (details: MaterialDetails) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(material?.name ?? '');
  const [sku, setSku] = useState(material?.sku ?? '');
  // On create the SKU follows the name until the admin edits it themselves.
  const [skuTouched, setSkuTouched] = useState(false);
  const [unitLabel, setUnitLabel] = useState(material?.unitLabel ?? 'pcs');
  const [packSize, setPackSize] = useState(
    material?.packSize != null ? String(material.packSize) : '',
  );
  const [cost, setCost] = useState(material?.cost ?? '');
  const [shelfLife, setShelfLife] = useState(
    material?.shelfLifeDays != null ? String(material.shelfLifeDays) : '',
  );
  const [description, setDescription] = useState(material?.description ?? '');
  const [localError, setLocalError] = useState<string | null>(null);
  // Shelf life only means something once receipts are tracked as stock lots.
  const featuresQuery = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const lotsEnabled = featuresQuery.data?.inventory_lots_enabled === true;

  const unit = unitLabel.trim() || 'pcs';

  function submit(e: React.FormEvent) {
    e.preventDefault();
    let pack: number | null = null;
    if (packSize.trim() !== '') {
      pack = Number(packSize);
      if (!Number.isInteger(pack) || pack <= 0) {
        setLocalError('Pack size must be a whole number greater than 0, or left blank.');
        return;
      }
    }
    if (cost.trim() !== '') {
      let valid = false;
      try {
        valid = new Decimal(cost.trim()).gte(0);
      } catch {
        valid = false;
      }
      if (!valid) {
        setLocalError('Cost must be a number, 0 or more, or left blank.');
        return;
      }
    }
    let shelfLifeDays: number | null = null;
    if (shelfLife.trim() !== '') {
      shelfLifeDays = Number(shelfLife);
      if (!Number.isInteger(shelfLifeDays) || shelfLifeDays <= 0) {
        setLocalError('Shelf life must be a whole number of days greater than 0, or left blank.');
        return;
      }
    }
    setLocalError(null);
    onSubmit({
      sku,
      name,
      description: description.trim() || undefined,
      unitLabel: unitLabel.trim() || null,
      packSize: pack,
      cost: cost.trim() === '' ? null : cost.trim(),
      // Omitted while hidden, so a save never clears a value the admin cannot see.
      ...(lotsEnabled ? { shelfLifeDays } : {}),
    });
  }

  const errMsg = localError ?? (error ? apiErrorMessage(error, 'Save failed') : undefined);

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-3 pt-2 md:grid-cols-2">
      <Field label="Name" htmlFor="material-name">
        <Input
          id="material-name"
          value={name}
          onChange={(e) => {
            const next = e.target.value;
            setName(next);
            if (!material && !skuTouched) setSku(skuFromName(next).slice(0, 64));
          }}
          maxLength={200}
          placeholder="12 oz cup"
          required
        />
      </Field>
      <Field label="SKU" htmlFor="material-sku">
        <Input
          id="material-sku"
          value={sku}
          onChange={(e) => {
            setSkuTouched(true);
            setSku(e.target.value.toUpperCase());
          }}
          maxLength={64}
          required
        />
        {material && normalizeSku(sku) !== normalizeSku(material.sku) && (
          <p className="mt-1 text-xs text-amber-600">
            Changing a live SKU invalidates printed labels carrying the old code.
          </p>
        )}
      </Field>

      <Field label="Counted in" htmlFor="material-unit">
        <Input
          id="material-unit"
          value={unitLabel}
          onChange={(e) => setUnitLabel(e.target.value)}
          maxLength={20}
          placeholder="pcs"
        />
        <p className="mt-1 text-xs text-muted-foreground">
          The unit the stock is counted in: pcs, g, ml. Stock is kept in whole numbers, so use the
          smallest unit you measure in (grams, not kilos).
        </p>
      </Field>
      <Field label="Pack size (optional)" htmlFor="material-pack">
        <Input
          id="material-pack"
          type="number"
          min="1"
          step="1"
          inputMode="numeric"
          value={packSize}
          onChange={(e) => setPackSize(e.target.value)}
          placeholder="none"
        />
        <p className="mt-1 text-xs text-muted-foreground">
          How many {unit} come in one purchase pack: 50 cups in a sleeve, 1000 g in a bag.
        </p>
      </Field>

      <Field label={`Cost per ${unit} (optional)`} htmlFor="material-cost">
        <MoneyInput
          id="material-cost"
          value={cost}
          onChange={(e) => setCost(e.target.value)}
          placeholder="unknown"
        />
        <p className="mt-1 text-xs text-muted-foreground">
          What one {unit} costs you. Leave blank if unknown; a 0 means it really is free.
        </p>
      </Field>
      {lotsEnabled && (
        <Field label="Shelf life in days (optional)" htmlFor="material-shelf-life">
          <Input
            id="material-shelf-life"
            type="number"
            min="1"
            step="1"
            inputMode="numeric"
            value={shelfLife}
            onChange={(e) => setShelfLife(e.target.value)}
            placeholder="does not expire"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            How long a delivery keeps. A receive that names no expiry date gets one this many days
            after it is received.
          </p>
        </Field>
      )}
      <div className="md:col-span-2">
        <Field label="Notes (optional)" htmlFor="material-description">
          <Textarea
            id="material-description"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
          />
        </Field>
      </div>

      {errMsg && <p className="text-sm text-destructive md:col-span-2">{errMsg}</p>}

      <div className="flex items-center justify-end gap-3 md:col-span-2">
        {saved && <span className="text-xs text-emerald-600">Saved.</span>}
        <Button type="button" variant="outline" onClick={onCancel}>
          Close
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
